import { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from './useAuth'
import companyTranslations from '../modules/i18n/locales/en-company.json'
import { TOptions } from 'i18next'

// Type for the translation object structure
type PartnerTranslationObject = typeof companyTranslations

// Type for all possible translation keys
export type PartnerTranslationKey = keyof PartnerTranslationObject

/**
 * Type-safe translation hook for partner-specific terminology
 * Automatically selects the correct translation namespace based on organizationType:
 * - "company" -> uses Position terminology (en-company namespace)
 * - "school" -> uses Class terminology (en-school namespace)
 * 
 * @example
 * ```tsx
 * const { t } = usePartnerTar();
 * 
 * // Type-safe usage
 * const newText = t('newCohort'); // ✅ Valid - returns "New Position" or "New Class"
 * const invalidText = t('invalidKey'); // ❌ TypeScript error
 * ```
 */
export function usePartnerTar() {
  const { organizationType } = useAuth()
  
  // Select the appropriate namespace based on organizationType
  const namespace = useMemo<'en-company' | 'en-school'>(() => {
    if (organizationType === 'school') {
      return 'en-school'
    }
    // Default to company namespace (for "company" or null/undefined)
    return 'en-company'
  }, [organizationType])

  const { t: i18nT } = useTranslation(namespace)

  /**
   * Type-safe translation function
   * @param key - Translation key (e.g., 'newCohort', 'cohort')
   * @returns Translated string
   */
  const t = useCallback(
    <K extends PartnerTranslationKey>(key: K, options?: TOptions): string => {
      return i18nT(key, options) as PartnerTranslationObject[K]
    },
    [i18nT]
  )

  return {
    t,
  }
}
